import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Highlight, themes } from 'prism-react-renderer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Brain,
  Rocket,
  Code2,
  Eye,
  Loader2,
  Sparkles,
  Globe,
  Settings,
  FileCode,
  FileText,
  Download,
  Palette,
  Type,
  Layout,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Lock,
  Crown
} from 'lucide-react';
import OrbisLogo from '@/components/OrbisLogo';
import OrbisSpinner from '@/components/ui/orbis-spinner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Legacy model list removed — backend picks optimal model via STAGE_MODEL_POOLS.
// Classic Mode always sends empty model = backend default.

const CodeBlock = ({ code, language }) => {
  if (!code) {
    return (
      <div className="flex items-center justify-center h-full text-[#5A5855]">
        <p>No code generated yet</p>
      </div>
    );
  }

  return (
    <Highlight theme={themes.nightOwl} code={code} language={language}>
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <pre className="text-sm leading-relaxed overflow-x-auto" style={{ ...style, background: 'transparent', margin: 0 }}>
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line })} className="table-row">
              <span className="table-cell text-right pr-4 text-[#5A5855] select-none w-10">
                {i + 1}
              </span>
              <span className="table-cell">
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </span>
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  );
};

const Preview = ({ html, css, js }) => {
  const [srcDoc, setSrcDoc] = useState('');

  useEffect(() => {
    if (html) {
      // Extract @import from CSS and convert to <link> tag (avoids visible text in sandboxed iframes)
      let linkTag = '';
      let cleanCss = css || '';
      const importMatch = cleanCss.match(/@import\s+url\(['"]?(.*?)['"]?\)\s*;?/);
      if (importMatch) {
        linkTag = `<link rel="stylesheet" href="${importMatch[1]}">`;
        cleanCss = cleanCss.replace(importMatch[0], '').trimStart();
      }
      const styleBlock = cleanCss ? `<style>${cleanCss}</style>` : '';
      const headInject = linkTag + styleBlock;

      const fullHtml = html.includes('<html')
        ? html.replace('</head>', `${headInject}</head>`).replace('</body>', `<script>${js || ''}</script></body>`)
        : `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${headInject}
</head>
<body>
  ${html}
  <script>${js || ''}</script>
</body>
</html>`;
      setSrcDoc(fullHtml);
    } else {
      setSrcDoc('');
    }
  }, [html, css, js]);

  if (!html) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-[#5A5855] bg-[var(--orbis-s1)]">
        <Eye className="w-12 h-12 mb-4 text-[#5A5855]" />
        <p>Generate code to see the preview</p>
      </div>
    );
  }

  return (
    <iframe
      srcDoc={srcDoc}
      className="w-full h-full bg-white"
      title="Preview"
      sandbox="allow-scripts"
      data-testid="preview-iframe"
    />
  );
};

// Color Swatch Component
const ColorSwatch = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <div 
      className="w-6 h-6 rounded border border-[rgba(255,255,255,0.12)]" 
      style={{ backgroundColor: color }}
    />
    <div className="flex flex-col">
      <span className="text-xs text-[#8A8882]">{label}</span>
      <span className="text-xs font-mono text-[#8A8882]">{color}</span>
    </div>
  </div>
);

// Design System Panel Component
const DesignSystemPanel = ({ designSystem, isLocked, onRegenerate, isRegenerating }) => {
  if (!designSystem) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-[#5A5855] p-4">
        <Palette className="w-12 h-12 mb-4 text-[#5A5855]" />
        <p className="text-center text-sm">Generate a design system first to see the visual guidelines</p>
      </div>
    );
  }

  const { colors, typography, style, pattern, anti_patterns, accessibility_checklist, premium_mode } = designSystem;

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-6">
        {/* Header with Lock/Regenerate */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-[#8A8882]" />
            <span className="text-sm font-normal text-[var(--orbis-warm)]">Design System</span>
            {premium_mode && (
              <Badge variant="outline" className="bg-amber-950/50 text-amber-400 border-amber-900/50 text-xs">
                <Crown className="w-3 h-3 mr-1" />
                Premium
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isLocked && <Lock className="w-3 h-3 text-[#5A5855]" />}
            <Button
              variant="ghost"
              size="sm"
              onClick={onRegenerate}
              disabled={isRegenerating || isLocked}
              className="h-7 text-xs text-[#8A8882] hover:text-[var(--orbis-warm)]"
              data-testid="regenerate-design-system-btn"
            >
              {isRegenerating ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
            </Button>
          </div>
        </div>

        {/* Style */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-[#8A8882] uppercase tracking-wider">
            <Layout className="w-3 h-3" />
            Style
          </div>
          <div className="bg-[var(--orbis-s1)] rounded-lg p-3 border border-[rgba(255,255,255,0.07)]">
            <p className="text-sm font-light text-[var(--orbis-warm)]">{style?.name || 'Minimalism'}</p>
            {style?.keywords && (
              <p className="text-xs text-[#5A5855] mt-1">{style.keywords}</p>
            )}
          </div>
        </div>

        {/* Color Palette */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-[#8A8882] uppercase tracking-wider">
            <Palette className="w-3 h-3" />
            Color Palette
          </div>
          <div className="bg-[var(--orbis-s1)] rounded-lg p-3 border border-[rgba(255,255,255,0.07)] space-y-3">
            <ColorSwatch color={colors?.primary || '#2563EB'} label="Primary" />
            <ColorSwatch color={colors?.secondary || '#3B82F6'} label="Secondary" />
            <ColorSwatch color={colors?.accent || '#F97316'} label="Accent/CTA" />
            <ColorSwatch color={colors?.background || '#FFFFFF'} label="Background" />
            <ColorSwatch color={colors?.text || '#1E293B'} label="Text" />
          </div>
        </div>

        {/* Typography */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-[#8A8882] uppercase tracking-wider">
            <Type className="w-3 h-3" />
            Typography
          </div>
          <div className="bg-[var(--orbis-s1)] rounded-lg p-3 border border-[rgba(255,255,255,0.07)] space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-[#5A5855]">Heading</span>
              <span className="text-xs font-normal text-[var(--orbis-warm)]">{typography?.heading || 'Inter'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-[#5A5855]">Body</span>
              <span className="text-xs font-normal text-[var(--orbis-warm)]">{typography?.body || 'Inter'}</span>
            </div>
            {typography?.mood && (
              <p className="text-xs text-[#5A5855] mt-1">Mood: {typography.mood}</p>
            )}
          </div>
        </div>

        {/* Layout Pattern */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-[#8A8882] uppercase tracking-wider">
            <Layout className="w-3 h-3" />
            Pattern
          </div>
          <div className="bg-[var(--orbis-s1)] rounded-lg p-3 border border-[rgba(255,255,255,0.07)]">
            <p className="text-sm font-light text-[var(--orbis-warm)]">{pattern?.name || 'Hero + Features'}</p>
            {pattern?.sections && (
              <p className="text-xs text-[#5A5855] mt-1">Sections: {pattern.sections}</p>
            )}
          </div>
        </div>

        {/* Anti-Patterns */}
        {anti_patterns && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-[#8A8882] uppercase tracking-wider">
              <AlertTriangle className="w-3 h-3" />
              Avoid
            </div>
            <div className="bg-red-950/20 rounded-lg p-3 border border-red-900/30">
              {anti_patterns.split('+').map((item, i) => (
                <p key={i} className="text-xs text-red-400 flex items-start gap-1">
                  <span>✕</span>
                  <span>{item.trim()}</span>
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Accessibility Checklist */}
        {accessibility_checklist && accessibility_checklist.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-[#8A8882] uppercase tracking-wider">
              <CheckCircle2 className="w-3 h-3" />
              UX Checklist
            </div>
            <div className="bg-[var(--orbis-s1)] rounded-lg p-3 border border-[rgba(255,255,255,0.07)] space-y-1">
              {accessibility_checklist.slice(0, 5).map((item, i) => (
                <p key={i} className="text-xs text-[#8A8882] flex items-start gap-1">
                  <span className="text-emerald-500">○</span>
                  <span>{item}</span>
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default function WorkspacePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isGeneratingDesignSystem, setIsGeneratingDesignSystem] = useState(false);
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [vibe, setVibe] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  
  // Design System state
  const [designSystem, setDesignSystem] = useState(null);
  const [useDesignSystem, setUseDesignSystem] = useState(true);
  const [premiumMode, setPremiumMode] = useState(false);
  const [isDesignSystemLocked, setIsDesignSystemLocked] = useState(false);
  
  // Code state
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [generatedCss, setGeneratedCss] = useState('');
  const [generatedJs, setGeneratedJs] = useState('');
  
  // UI state
  const [activeCodeTab, setActiveCodeTab] = useState('html');
  const [activeRightTab, setActiveRightTab] = useState('preview');
  const [activeLeftTab, setActiveLeftTab] = useState('brief');

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}`);
      if (response.data.magic_mode) {
        navigate(`/magic/${projectId}`, { replace: true });
        return;
      }
      setProject(response.data);
      setCustomDomain(response.data.custom_domain || '');
      if (response.data.generated_html) {
        setGeneratedHtml(response.data.generated_html);
        setGeneratedCss(response.data.generated_css || '');
        setGeneratedJs(response.data.generated_js || '');
      }
      
      // Fetch design system if exists
      try {
        const dsResponse = await axios.get(`${API_URL}/design-system/${projectId}`);
        if (dsResponse.data.has_design_system) {
          setDesignSystem(dsResponse.data.design_system);
        }
      } catch (e) {
        // Design system might not exist yet
      }
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateDesignSystem = async () => {
    if (!companyName || !industry) {
      toast.error('Please fill in Company Name and Industry first');
      return;
    }

    setIsGeneratingDesignSystem(true);
    try {
      const response = await axios.post(`${API_URL}/design-system/generate`, {
        project_id: projectId,
        project_name: companyName,
        company_type: industry,
        niche: industry,
        audience: '',
        vibe: vibe,
        stack: 'React + Tailwind',
        pages: ['landing'],
        premium_mode: premiumMode
      });
      
      setDesignSystem(response.data.design_system);
      toast.success('Design system generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate design system');
    } finally {
      setIsGeneratingDesignSystem(false);
    }
  };

  const handleGenerate = async () => {
    if (!companyName || !industry || !vibe) {
      toast.error('Please fill in all fields');
      return;
    }

    // If using design system and it doesn't exist, generate it first
    if (useDesignSystem && !designSystem) {
      await handleGenerateDesignSystem();
    }

    setIsGenerating(true);
    try {
      const endpoint = useDesignSystem ? `${API_URL}/generate-with-design-system` : `${API_URL}/generate`;
      const response = await axios.post(endpoint, {
        project_id: projectId,
        company_name: companyName,
        industry,
        vibe,
        model: selectedModel,
        use_design_system: useDesignSystem,
        premium_mode: premiumMode
      });
      
      setGeneratedHtml(response.data.html);
      setGeneratedCss(response.data.css);
      setGeneratedJs(response.data.js);
      setActiveRightTab('preview');
      
      // Refresh project to get updated status
      const projectResponse = await axios.get(`${API_URL}/projects/${projectId}`);
      setProject(projectResponse.data);
      
      // Refresh design system
      if (useDesignSystem) {
        try {
          const dsResponse = await axios.get(`${API_URL}/design-system/${projectId}`);
          if (dsResponse.data.has_design_system) {
            setDesignSystem(dsResponse.data.design_system);
          }
        } catch (e) {}
      }
      
      toast.success('Website generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeploy = async () => {
    if (!customDomain) {
      toast.error('Please set a custom domain first');
      return;
    }

    if (!generatedHtml) {
      toast.error('Please generate code first');
      return;
    }

    if (customDomain !== project?.custom_domain) {
      try {
        await axios.patch(`${API_URL}/projects/${projectId}`, {
          custom_domain: customDomain
        });
      } catch (error) {
        toast.error('Failed to update domain');
        return;
      }
    }

    setIsDeploying(true);
    try {
      const response = await axios.post(`${API_URL}/deploy`, {
        project_id: projectId
      });
      
      const projectResponse = await axios.get(`${API_URL}/projects/${projectId}`);
      setProject(projectResponse.data);
      
      toast.success(response.data.message);
      
      if (response.data.url) {
        toast.info(`Your site is live at ${response.data.url}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deployment failed');
    } finally {
      setIsDeploying(false);
    }
  };

  const handleDownload = async () => {
    if (!generatedHtml) {
      toast.error('Please generate code first');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project?.name || 'website'}_code.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Code downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download code');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--orbis-bg)] flex items-center justify-center">
        <OrbisSpinner size={64} />
      </div>
    );
  }

  return (
    <div className="h-screen bg-[var(--orbis-bg)] flex flex-col overflow-hidden">
      {/* Top Navbar */}
      <nav className="navbar-blur border-b border-[rgba(255,255,255,0.07)] bg-[var(--orbis-bg)]/80 h-14 flex items-center px-4 shrink-0">
        <div className="flex items-center gap-4 flex-1">
          <Button
            variant="ghost"
            size="icon"
            className="text-[#8A8882] hover:text-[var(--orbis-warm)] hover:bg-[var(--orbis-s2)]"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-3">
            <OrbisLogo size={24} variant="full" />
            <span className="text-[var(--orbis-warm)] font-normal">{project?.name}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.07)] rounded-md">
            <Globe className="h-4 w-4 text-[#5A5855]" />
            <Input
              placeholder="domain.com"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              className="bg-transparent border-0 h-auto p-0 text-sm text-[#8A8882] placeholder:text-[#5A5855] focus-visible:ring-0 w-40"
              data-testid="domain-input"
            />
          </div>
          
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={!generatedHtml}
            className="border-[rgba(255,255,255,0.07)] text-[#8A8882] hover:bg-[var(--orbis-s2)] hover:text-[var(--orbis-warm)] gap-2 btn-press"
            data-testid="download-btn"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
          
          <Button
            onClick={handleDeploy}
            disabled={isDeploying || !generatedHtml}
            className="bg-[var(--orbis-blue)] text-[var(--orbis-bg)] hover:bg-[var(--orbis-blue)]/80 font-normal gap-2 btn-press"
            data-testid="deploy-btn"
          >
            {isDeploying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deploying...
              </>
            ) : (
              <>
                <Rocket className="w-4 h-4" />
                Deploy
              </>
            )}
          </Button>
        </div>
      </nav>

      {/* Main Workspace */}
      <PanelGroup direction="horizontal" className="flex-1">
        {/* Left Panel - Brief & Design System */}
        <Panel defaultSize={30} minSize={20} maxSize={50}>
          <div className="h-full bg-[var(--orbis-bg)] border-r border-[rgba(255,255,255,0.07)] flex flex-col">
            <Tabs value={activeLeftTab} onValueChange={setActiveLeftTab} className="flex flex-col h-full">
              <div className="border-b border-[rgba(255,255,255,0.07)] px-2">
                <TabsList className="bg-transparent h-10 gap-2">
                  <TabsTrigger 
                    value="brief" 
                    className="data-[state=active]:bg-[var(--orbis-s2)] data-[state=active]:text-[var(--orbis-warm)] text-[#5A5855] text-xs gap-1.5 h-7"
                    data-testid="brief-tab"
                  >
                    <Settings className="w-3 h-3" />
                    Brief
                  </TabsTrigger>
                  <TabsTrigger 
                    value="design" 
                    className="data-[state=active]:bg-[var(--orbis-s2)] data-[state=active]:text-[var(--orbis-warm)] text-[#5A5855] text-xs gap-1.5 h-7"
                    data-testid="design-tab"
                  >
                    <Palette className="w-3 h-3" />
                    Design
                    {designSystem && <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />}
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="brief" className="flex-1 mt-0 overflow-hidden">
                <ScrollArea className="h-full">
                  <div className="p-4 space-y-6">
                    <div className="space-y-2">
                      <Label className="text-[#8A8882] text-xs uppercase tracking-wider">Company Name</Label>
                      <Input
                        placeholder="Acme Corporation"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="bg-[var(--orbis-s1)] border-[rgba(255,255,255,0.07)] text-[var(--orbis-warm)] placeholder:text-[#5A5855] focus:border-[rgba(255,255,255,0.12)]"
                        data-testid="company-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#8A8882] text-xs uppercase tracking-wider">Industry</Label>
                      <Input
                        placeholder="Technology, Healthcare, Finance..."
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        className="bg-[var(--orbis-s1)] border-[rgba(255,255,255,0.07)] text-[var(--orbis-warm)] placeholder:text-[#5A5855] focus:border-[rgba(255,255,255,0.12)]"
                        data-testid="industry-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#8A8882] text-xs uppercase tracking-wider">Vibe / Style</Label>
                      <Textarea
                        placeholder="Modern, minimalist, dark mode with subtle animations..."
                        value={vibe}
                        onChange={(e) => setVibe(e.target.value)}
                        className="bg-[var(--orbis-s1)] border-[rgba(255,255,255,0.07)] text-[var(--orbis-warm)] placeholder:text-[#5A5855] focus:border-[rgba(255,255,255,0.12)] min-h-[80px] resize-none"
                        data-testid="vibe-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#8A8882] text-xs uppercase tracking-wider flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        AI Brain
                      </Label>
                      <p style={{ fontSize: '13px', color: 'var(--orbis-t2)', padding: '8px 0' }}>Auto-selected for best results</p>
                    </div>

                    {/* Design System Toggle */}
                    <div className="space-y-3 pt-2 border-t border-[rgba(255,255,255,0.07)]">
                      <div className="flex items-center justify-between">
                        <Label className="text-[#8A8882] text-xs uppercase tracking-wider flex items-center gap-2">
                          <Palette className="w-4 h-4" />
                          Use Design System
                        </Label>
                        <Switch
                          checked={useDesignSystem}
                          onCheckedChange={setUseDesignSystem}
                          data-testid="use-design-system-toggle"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label className="text-[#8A8882] text-xs uppercase tracking-wider flex items-center gap-2">
                          <Crown className="w-4 h-4 text-amber-500" />
                          Premium UI Mode
                        </Label>
                        <Switch
                          checked={premiumMode}
                          onCheckedChange={setPremiumMode}
                          data-testid="premium-mode-toggle"
                        />
                      </div>
                    </div>

                    {/* Generate Design System Button */}
                    {useDesignSystem && (
                      <Button
                        variant="outline"
                        onClick={handleGenerateDesignSystem}
                        disabled={isGeneratingDesignSystem || !companyName || !industry}
                        className="w-full border-[rgba(255,255,255,0.07)] text-[#8A8882] hover:bg-[var(--orbis-s2)] hover:text-[var(--orbis-warm)] gap-2"
                        data-testid="generate-design-system-btn"
                      >
                        {isGeneratingDesignSystem ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Palette className="w-4 h-4" />
                            {designSystem ? 'Regenerate' : 'Generate'} Design System
                          </>
                        )}
                      </Button>
                    )}

                    <Button
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="w-full bg-[var(--orbis-blue)] text-[var(--orbis-bg)] hover:bg-[var(--orbis-blue)]/80 font-normal gap-2 h-11 btn-press"
                      data-testid="generate-btn"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Generate Website
                        </>
                      )}
                    </Button>
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="design" className="flex-1 mt-0 overflow-hidden">
                <DesignSystemPanel 
                  designSystem={designSystem}
                  isLocked={isDesignSystemLocked}
                  onRegenerate={handleGenerateDesignSystem}
                  isRegenerating={isGeneratingDesignSystem}
                />
              </TabsContent>
            </Tabs>
          </div>
        </Panel>

        <PanelResizeHandle className="w-1 bg-[var(--orbis-s2)]/60 hover:bg-[var(--orbis-s2)] transition-colors" />

        {/* Right Panel - Code & Preview */}
        <Panel defaultSize={70} minSize={50}>
          <div className="h-full bg-[var(--orbis-s1)] flex flex-col">
            <Tabs value={activeRightTab} onValueChange={setActiveRightTab} className="flex flex-col h-full">
              <div className="border-b border-[rgba(255,255,255,0.07)] px-4">
                <TabsList className="bg-transparent h-12 gap-4">
                  <TabsTrigger 
                    value="code" 
                    className="data-[state=active]:bg-transparent data-[state=active]:text-[var(--orbis-warm)] data-[state=active]:shadow-none text-[#5A5855] hover:text-[#8A8882] gap-2 relative"
                    data-testid="code-tab"
                  >
                    <Code2 className="w-4 h-4" />
                    Code
                    {activeRightTab === 'code' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-t" />}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="preview" 
                    className="data-[state=active]:bg-transparent data-[state=active]:text-[var(--orbis-warm)] data-[state=active]:shadow-none text-[#5A5855] hover:text-[#8A8882] gap-2 relative"
                    data-testid="preview-tab"
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                    {activeRightTab === 'preview' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-t" />}
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="code" className="flex-1 mt-0 overflow-hidden">
                <div className="h-full flex flex-col">
                  <div className="border-b border-[rgba(255,255,255,0.07)] px-4 py-2 flex gap-2">
                    <Button
                      variant={activeCodeTab === 'html' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setActiveCodeTab('html')}
                      className={`gap-2 ${activeCodeTab === 'html' ? 'bg-[var(--orbis-s2)] text-[var(--orbis-warm)]' : 'text-[#5A5855] hover:text-[var(--orbis-warm)]'}`}
                      data-testid="html-tab-btn"
                    >
                      <FileCode className="w-4 h-4" />
                      index.html
                    </Button>
                    <Button
                      variant={activeCodeTab === 'css' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setActiveCodeTab('css')}
                      className={`gap-2 ${activeCodeTab === 'css' ? 'bg-[var(--orbis-s2)] text-[var(--orbis-warm)]' : 'text-[#5A5855] hover:text-[var(--orbis-warm)]'}`}
                      data-testid="css-tab-btn"
                    >
                      <FileText className="w-4 h-4" />
                      style.css
                    </Button>
                    <Button
                      variant={activeCodeTab === 'js' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setActiveCodeTab('js')}
                      className={`gap-2 ${activeCodeTab === 'js' ? 'bg-[var(--orbis-s2)] text-[var(--orbis-warm)]' : 'text-[#5A5855] hover:text-[var(--orbis-warm)]'}`}
                      data-testid="js-tab-btn"
                    >
                      <FileCode className="w-4 h-4" />
                      script.js
                    </Button>
                  </div>
                  <ScrollArea className="flex-1 bg-[var(--orbis-bg)]">
                    <div className="p-4">
                      {activeCodeTab === 'html' && <CodeBlock code={generatedHtml} language="html" />}
                      {activeCodeTab === 'css' && <CodeBlock code={generatedCss} language="css" />}
                      {activeCodeTab === 'js' && <CodeBlock code={generatedJs} language="javascript" />}
                    </div>
                  </ScrollArea>
                </div>
              </TabsContent>

              <TabsContent value="preview" className="flex-1 mt-0 p-4 overflow-hidden">
                <div className="h-full rounded-lg overflow-hidden border border-[rgba(255,255,255,0.07)]">
                  <Preview html={generatedHtml} css={generatedCss} js={generatedJs} />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
}
